import {
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ComponentType,
} from 'react';
import {
	Accessibility,
	BookOpen,
	ChartColumn,
	HardDrive,
	Headphones,
	Info,
	Search,
	Settings2,
	Shield,
	UserRound,
	X,
} from 'lucide-react';
import { toast } from 'sonner';

import { AppContext } from '@/app.context';
import { takeCacheClearedNotice } from '@features/clear-app-data';
import { cn } from '@/lib/utils';
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogTitle,
} from '@shared/ui/dialog';
import {
	useSettingsNavigation,
	useSettingsPage,
} from '@shared/lib/settings-route';
import { useT, type MessageKey } from '@shared/i18n';

import {
	SETTINGS_GROUPS,
	isSettingAvailable,
} from '../model/settings-registry';
import {
	findPage,
	localizeSettings,
	searchSettings,
	withAvailableSettings,
	type SettingsPage,
	type SettingsSearchResult,
} from '../model/settings-search';
import { SETTING_CONTROLS } from './setting-controls';

import styles from './settings-dialog.module.css';

/** One icon per page id in `model/settings-registry.ts`. */
const PAGE_ICONS: Record<string, ComponentType<{ 'aria-hidden'?: boolean }>> =
	{
		general: Settings2,
		account: UserRound,
		privacy: Shield,
		accessibility: Accessibility,
		storage: HardDrive,
		about: Info,
		stats: ChartColumn,
		reading: BookOpen,
		audio: Headphones,
	};

/** How long a row picked from search stays highlighted. */
const HIGHLIGHT_MS = 1600;

/**
 * The app's Settings, modelled on Claude's: a modal over the current page,
 * grouped pages in a left column (a tab strip on phones), the selected
 * page's sections on the right, and a search that jumps to a setting.
 *
 * Mounted once, in the root route, and driven by `?settings=<page>` (see
 * `@shared/lib/settings-route`) -- any menu opens it with
 * `useSettingsNavigation().openSettings()`, and every page is a link.
 */
export function SettingsDialog() {
	const requestedPage = useSettingsPage();
	const { isTouchable } = useContext(AppContext);
	const { selectPage, closeSettings } = useSettingsNavigation();

	const t = useT();

	// The first load after Clear cache says it worked (Settings is mounted
	// on every route, so this runs wherever the reload lands).
	useEffect(() => {
		if (takeCacheClearedNotice()) {
			toast.success(t('settings.clearCache.done'));
		}
	}, [t]);

	const groups = useMemo(
		() =>
			localizeSettings(
				withAvailableSettings(SETTINGS_GROUPS, isSettingAvailable),
				(key) => t(key as MessageKey),
			),
		[t],
	);
	const firstPage = groups[0]?.pages[0];

	// Kept while the dialog animates out, when the param is already gone.
	const [shownPage, setShownPage] = useState<SettingsPage | undefined>();
	useEffect(() => {
		if (requestedPage !== null) {
			// An unknown or unavailable page (an old link, a tenant without
			// that setting) falls back to the first page instead of an empty
			// dialog.
			setShownPage(findPage(groups, requestedPage) ?? firstPage);
		}
	}, [requestedPage, groups, firstPage]);

	const [query, setQuery] = useState('');
	const results = useMemo(() => searchSettings(groups, query), [groups, query]);
	const isSearching = query.trim().length > 0;

	const [highlighted, setHighlighted] = useState<string | null>(null);
	const open = requestedPage !== null && !!firstPage;

	useEffect(() => {
		if (!open) {
			setQuery('');
			setHighlighted(null);
		}
	}, [open]);

	// Scrolls a row picked from search into view once its page renders.
	useEffect(() => {
		if (!highlighted) {
			return;
		}
		document
			.getElementById(rowId(highlighted))
			?.scrollIntoView({ block: 'center' });
		const timeout = setTimeout(() => setHighlighted(null), HIGHLIGHT_MS);
		return () => clearTimeout(timeout);
	}, [highlighted]);

	const onPickResult = (result: SettingsSearchResult) => {
		selectPage(result.page.id);
		setShownPage(result.page);
		setQuery('');
		setHighlighted(result.setting.id);
	};

	const searchRef = useRef<HTMLInputElement>(null);
	const titleRef = useRef<HTMLHeadingElement>(null);

	if (!firstPage) {
		return null;
	}
	const page = shownPage ?? firstPage;

	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				if (!next) {
					closeSettings();
				}
			}}
		>
			<DialogContent
				showCloseButton={false}
				className={styles['dialog']}
				backdropClassName={styles['backdrop']}
				// Search first with a mouse and keyboard; on a touch screen a
				// focused input would pop the keyboard over the page the reader
				// came to change -- however the dialog was opened, including
				// from a link.
				initialFocus={() =>
					isTouchable ? titleRef.current : searchRef.current
				}
			>
				<div
					className={styles['layout']}
					data-searching={isSearching || undefined}
				>
					<div className={styles['sidebar']}>
						<div className={styles['header']}>
							<DialogTitle
								ref={titleRef}
								tabIndex={-1}
								className={styles['title']}
							>
								{t('common.settings')}
							</DialogTitle>
							<DialogClose
								className={styles['close']}
								aria-label={t('settings.close')}
							>
								<X aria-hidden="true" />
							</DialogClose>
						</div>
						<div className={styles['search']}>
							<Search aria-hidden="true" />
							<input
								ref={searchRef}
								type="search"
								placeholder={t('settings.search')}
								aria-label={t('settings.search')}
								value={query}
								onChange={(event) => setQuery(event.target.value)}
							/>
						</div>
						{isSearching ? (
							<SearchResults results={results} onPick={onPickResult} />
						) : (
							<nav className={styles['nav']} aria-label={t('settings.pages')}>
								{groups.map((group) => (
									<div key={group.id} className={styles['nav-group']}>
										<span id={`settings-group-${group.id}`}>
											{group.label}
										</span>
										<ul aria-labelledby={`settings-group-${group.id}`}>
											{group.pages.map((item) => {
												const Icon = PAGE_ICONS[item.id];
												const current = item.id === page.id;
												return (
													<li key={item.id}>
														<button
															type="button"
															aria-current={current ? 'page' : undefined}
															onClick={() => {
																selectPage(item.id);
																setShownPage(item);
															}}
														>
															{Icon ? <Icon aria-hidden /> : null}
															<span>{item.label}</span>
														</button>
													</li>
												);
											})}
										</ul>
									</div>
								))}
							</nav>
						)}
					</div>
					<div className={styles['content']}>
						<h3 className={styles['page-title']}>{page.label}</h3>
						{page.sections.map((section) => (
							<section
								key={section.id}
								aria-labelledby={`settings-section-${section.id}`}
								className={styles['section']}
							>
								<h4
									id={`settings-section-${section.id}`}
									className={section.hideTitle ? 'sr-only' : undefined}
								>
									{section.title}
								</h4>
								{section.settings.map((setting) => {
									const Control = SETTING_CONTROLS[setting.id];
									return (
										<div
											key={setting.id}
											id={rowId(setting.id)}
											className={styles['row']}
											data-layout={setting.layout}
											data-highlighted={
												highlighted === setting.id || undefined
											}
										>
											<div className={styles['row-text']}>
												<span
													id={labelId(setting.id)}
													className={cn(
														styles['row-label'],
														// Its description, if any, still shows.
														setting.hideLabel && 'sr-only',
													)}
												>
													{setting.label}
												</span>
												{setting.description ? (
													<span
														id={descriptionId(setting.id)}
														className={styles['row-description']}
													>
														{setting.description}
													</span>
												) : null}
											</div>
											<div className={styles['row-control']}>
												{Control ? (
													<Control
														labelId={labelId(setting.id)}
														descriptionId={
															setting.description
																? descriptionId(setting.id)
																: undefined
														}
													/>
												) : null}
											</div>
										</div>
									);
								})}
							</section>
						))}
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}

function SearchResults({
	results,
	onPick,
}: {
	results: Array<SettingsSearchResult>;
	onPick: (result: SettingsSearchResult) => void;
}) {
	const t = useT();
	if (!results.length) {
		return (
			<output className={styles['no-results']}>{t('settings.noResults')}</output>
		);
	}
	return (
		<ul className={styles['results']} aria-label={t('settings.results')}>
			{results.map((result) => (
				<li key={result.setting.id}>
					<button type="button" onClick={() => onPick(result)}>
						<span>{result.setting.label}</span>
						<span>
							{result.page.label} › {result.section.title}
						</span>
					</button>
				</li>
			))}
		</ul>
	);
}

function rowId(settingId: string) {
	return `settings-row-${settingId}`;
}

function labelId(settingId: string) {
	return `settings-label-${settingId}`;
}

function descriptionId(settingId: string) {
	return `settings-description-${settingId}`;
}
